<template>
  <div ref="containerElement" class="w-full" style="text-align: left" />
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import * as monaco from 'monaco-editor'

import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { programLanguageCodeMap } from '@/util/language/programLangugageCodeMap'

monaco.languages.register({
  id: 'haskell'
})
monaco.languages.register({
  id: 'd'
})
monaco.languages.register({
  id: 'scala'
})
monaco.languages.register({
  id: 'ocaml'
})

export default defineComponent({
  props: {
    value: {
      type: String,
      required: true
    }
  },
  setup(props, ctx) {
    const containerElement = ref<HTMLElement | null>(null)
    let editor: monaco.editor.IStandaloneCodeEditor
    const { editorFontSize, programLanguage, viewMode } = useConfigStatePresenter()

    const initMonaco = () => {
      if (containerElement.value != null) {
        editor = monaco.editor.create(containerElement.value, {
          theme: viewMode.value == 'LIGHT' ? 'vs' : 'vs-dark',
          value: props.value,
          language: programLanguageCodeMap[programLanguage.value].code,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          readOnly: true,
          lineNumbers: 'on',
          lineNumbersMinChars: 3,
          lineDecorationsWidth: 0,
          minimap: { enabled: false }
        })

        setFontSize(editorFontSize.value)
      }
    }

    const destroyMonaco = () => {
      if (typeof editor !== 'undefined') {
        editor.dispose()
      }
    }

    const setFontSize = (size: number) => {
      let options = { fontSize: size }
      editor.updateOptions(options)
    }

    watch(
      () => viewMode.value,
      () => {
        destroyMonaco()
        initMonaco()
      }
    )

    watch(
      () => editorFontSize.value,
      () => {
        setFontSize(editorFontSize.value)
      }
    )

    watch(
      () => props.value,
      newValue => {
        if (editor) {
          if (newValue !== editor.getValue()) {
            editor.setValue(newValue)
          }
        }
      }
    )

    onMounted(() => {
      initMonaco()
    })

    onBeforeUnmount(() => {
      destroyMonaco()
    })

    return {
      containerElement
    }
  }
})
</script>
